import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/prepaids/NewPrepaid.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon, PlusIcon } from "@heroicons/react/24/solid";

import { Button } from "../../components/Button";
import { AppleComboBox } from "../../components/AppleComboBox";
import { apiGet, apiPost } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import VendorEntry from "../VendorEntry";
import { formatAnyDateToLocale, ymdTodayLocal } from "../../utils/dateUtils";
import { A } from "../../styles/appleClasses";
import { AppleDateInput } from "../../components/AppleDateInput";
import { AppleNumberInput } from "../../components/AppleNumberInput";
import MemoSection from "../../components/MemoSection";
import {
  extractApiDetailOutcome,
  isClosedPeriodError,
  isDateClosed,
} from "../../utils/closedPeriod";
import {
  IDLE_PREPAID_CREATE_ATTEMPT,
  PrepaidCreateAttempt,
  canonicalPrepaidCreate,
  fingerprintPrepaidCreate,
  randomUuidKey,
  decidePrepaidCreateAttempt,
  resolvePrepaidCreateSuccess,
  resolvePrepaidCreateDefiniteRejection,
  resolvePrepaidCreateAmbiguousFailure,
  shouldRestoreAmbiguousAttempt,
  classifyPrepaidCreateFailure,
} from "../../utils/prepaidCreateIdempotency";

/* ───────────────────── Types ───────────────────── */
type Vendor = {
  id: number | string;
  name: string;
  email?: string | null;
  mobile?: string | null;
  contact?: string | null;
  address?: string | null;
};

type Account = {
  id: number | string;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

/* ───────────────────── Utils ───────────────────── */
const lower = (s?: string | null) => (s || "").toLowerCase();
const accountDisplayName = (a?: Account | null) =>
  lower(a?.kind) === "credit_card_payable" ? "Credit Card" : a?.name || "";
const PREPAIDS_PATH = "/prepaids";

async function fetchBookDate(company_id: string): Promise<string> {
  const candidates = ["/book-date", "/settings/book-date", "/settings"];
  for (const base of candidates) {
    try {
      const sep = base.includes("?") ? "&" : "?";
      const res: any = await apiGet<any>(
        `${base}${sep}company_id=${company_id}`,
      );
      const d =
        typeof res === "string"
          ? res
          : res?.date || res?.book_date || res?.current_date || res?.today;
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    } catch {
      // continue
    }
  }
  return ymdTodayLocal();
}

/* ───────────────────── Page ───────────────────── */
export default function NewPrepaid() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendor, setVendor] = useState<Vendor | null>(null);

  const [paymentAccounts, setPaymentAccounts] = useState<Account[]>([]);
  const [paymentAccount, setPaymentAccount] = useState<Account | null>(null);

  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(ymdTodayLocal());
  const [memo, setMemo] = useState("");

  const [saving, setSaving] = useState(false);
  type DisplayError = { key: ParseKeys; date?: string | null } | { raw: string } | null;
  const [error, setError] = useState<DisplayError>(null);
  const displayError = error == null ? "" : "raw" in error ? error.raw
    : t(error.key, error.key === "sales.errors.closedSubmit" ? {
        date: error.date ? formatAnyDateToLocale(error.date.slice(0, 10))
          : t("sales.errors.closedDate"),
      } : {});
  const apiDisplayError = (e: any): DisplayError => {
    const outcome = extractApiDetailOutcome(e);
    return outcome.kind === "generic" ? { key: "sales.errors.generic" }
      : outcome.text ? { raw: outcome.text } : { key: "newPrepaid.saveFailed" };
  };

  // Provenance is separate from raw names, so real names resembling fallbacks stay raw.
  const syntheticVendors = useRef(new WeakSet<Vendor>());
  const syntheticAccounts = useRef(new WeakSet<Account>());
  const vendorDisplayName = (v: Vendor) => syntheticVendors.current.has(v)
    ? t("newDeposit.vendorFallback", { id: v.id }) : v.name;
  const paymentDisplayName = (a: Account) => syntheticAccounts.current.has(a)
    ? t("newDeposit.accountFallback", { id: a.id })
    : lower(a.kind) === "credit_card_payable" ? t("expenses.entry.creditCard") : a.name || "";

  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );
  const [showNewVendor, setShowNewVendor] = useState(false);

  // Prepaid-create idempotency attempt lifecycle (see
  // utils/prepaidCreateIdempotency.ts). A ref, not state: must be stable
  // across renders, must not itself trigger a re-render, and must be
  // readable synchronously by the in-flight guard below. NewPrepaid
  // remounts fresh every time the user navigates to /prepaids/new, so a
  // single attempt slot per mounted instance is sufficient -- no
  // resource-scoped attempt map is needed (contrast with
  // PrepaidDetail.tsx, which is mounted once for the route and reused
  // across different prepaid_id values).
  const prepaidCreateAttemptRef = useRef<PrepaidCreateAttempt>(
    IDLE_PREPAID_CREATE_ATTEMPT,
  );
  // Purely synchronous double-click/repeat-tap guard -- independent of the
  // attempt state machine above.
  const prepaidCreateInFlightRef = useRef(false);

  const labelCls = `${A.label} block mb-1`;
  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition`;

  // Date init (querystring date -> server book-date -> today)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!company_id) return;

      const qs = new URLSearchParams(location.search);
      const d = qs.get("date");
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        if (mounted) setDate(d);
        return;
      }

      const srv = await fetchBookDate(company_id);
      if (mounted) setDate(srv);
    })();

    return () => {
      mounted = false;
    };
  }, [company_id, location.search]);

  // Vendors
  useEffect(() => {
    if (!company_id) return;

    apiGet<Vendor[]>(`/vendors?company_id=${company_id}`)
      .then((xs) =>
        setVendors((xs || []).sort((a, b) => a.name.localeCompare(b.name))),
      )
      .catch(() => setVendors([]));

    setVendor(null);
  }, [company_id]);

  // Payment sources — cash/bank plus system Credit Card Payable.
  useEffect(() => {
    if (!company_id) return;

    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((xs) => {
        const allowed = new Set(["cash", "bank"]);
        const excluded = new Set(["cash_system", "bank_system"]);

        const filtered = (xs || [])
          .filter((a) => {
            const type = lower(a.type);
            const kind = lower(a.kind);
            if (type === "liability" && kind === "credit_card_payable")
              return true;
            if (type !== "asset") return false;
            if (a.is_system) return false;
            if (excluded.has(kind)) return false;
            return allowed.has(kind);
          })
          .sort((a, b) => {
            const kindRank = (x: Account) => (lower(x.kind) === "cash" ? 0 : 1);

            const rankDiff = kindRank(a) - kindRank(b);
            if (rankDiff !== 0) return rankDiff;

            return a.name.localeCompare(b.name);
          });

        setPaymentAccounts(filtered);
        setPaymentAccount((prev) => {
          if (prev && filtered.find((b) => String(b.id) === String(prev.id)))
            return prev;
          return filtered[0] || null;
        });
      })
      .catch(() => {
        setPaymentAccounts([]);
        setPaymentAccount(null);
      });
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  const paymentPlaceholder = useMemo(
    () =>
      paymentAccounts.length
        ? t("newDeposit.selectSource")
        : t("newDeposit.noSources"),
    [paymentAccounts.length, t],
  );

  const isClosedDate = useMemo(() => {
    return isDateClosed(date, closedThroughDate);
  }, [date, closedThroughDate]);

  const onAmountBlur = () => {
    setAmount(amount.trim());
  };

  // Force the form fields back to EXACTLY what was submitted under an
  // ambiguous (still-unresolved) attempt -- used both when a mismatched
  // edited retry is blocked, and right after an ambiguous failure is
  // detected. Mirrors NewDeposit.tsx's restoreSubmittedPayload.
  const restoreSubmittedPayload = (attempt: PrepaidCreateAttempt) => {
    if (!shouldRestoreAmbiguousAttempt(attempt) || !attempt.submittedPayload) {
      return;
    }
    const payload = attempt.submittedPayload;

    if (payload.vendor_id) {
      const matchedVendor =
        vendors.find((v) => String(v.id) === String(payload.vendor_id)) || {
          id: payload.vendor_id,
          name: `Vendor #${payload.vendor_id}`,
        };
      if (!vendors.some((v) => v === matchedVendor)) syntheticVendors.current.add(matchedVendor);
      setVendor(matchedVendor);
    } else {
      setVendor(null);
    }

    const matchedAccount =
      paymentAccounts.find(
        (a) => String(a.id) === String(payload.bank_account_id),
      ) || { id: payload.bank_account_id, name: `Account #${payload.bank_account_id}` };
    if (!paymentAccounts.some((a) => a === matchedAccount)) syntheticAccounts.current.add(matchedAccount);
    setPaymentAccount(matchedAccount);

    setAmount(payload.amount);
    setDate(payload.date);
    setMemo(payload.memo || "");
  };

  const submit = async () => {
    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `saving` state alone can't catch this
    // since it only updates on the next render.
    if (prepaidCreateInFlightRef.current) return;

    setError(null);

    const amt = parseFloat(amount);
    if (!company_id) return setError({ key: "newDeposit.invalidCompany" });
    if (!paymentAccount) return setError({ key: "newDeposit.selectAccountError" });
    if (isNaN(amt) || amt <= 0)
      return setError({ key: "newDeposit.positiveAmount" });

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether this is a first attempt (mint a new
    // key), an unchanged retry of a still-ambiguous attempt (reuse its
    // key), or an edited retry of one (block, no network call, restore the
    // original submitted values).
    const canonicalPayload = canonicalPrepaidCreate(
      vendor ? vendor.id : null,
      paymentAccount.id,
      amt,
      date,
      memo || "",
    );
    const fingerprint = fingerprintPrepaidCreate(canonicalPayload);
    const decision = decidePrepaidCreateAttempt(
      prepaidCreateAttemptRef.current,
      canonicalPayload,
      fingerprint,
      randomUuidKey,
    );

    if (decision.kind === "blocked-mismatch") {
      restoreSubmittedPayload(prepaidCreateAttemptRef.current);
      setError({ key: "newPrepaid.editedRetry" });
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- prepaidCreateInFlightRef.current already
      // returns above before this point. Kept for correctness of the pure
      // decision function itself.
      return;
    }

    prepaidCreateAttemptRef.current = decision.attempt;
    prepaidCreateInFlightRef.current = true;

    setSaving(true);
    try {
      // Send the SAME canonicalPayload object used to build the
      // fingerprint above -- never a separately reconstructed copy --
      // plus idempotency_key.
      const created: any = await apiPost("/prepaids", {
        ...canonicalPayload,
        idempotency_key: decision.idempotencyKey,
        company_id,
      });

      // Confirmed success (create or replay -- both return the same
      // response shape): clear the attempt so the next prepaid gets a
      // fresh key instead of leaking this one into it.
      prepaidCreateAttemptRef.current = resolvePrepaidCreateSuccess();

      const newId = created?.id;
      if (newId) {
        navigate(companyRoute(company_id, `/prepaids/${newId}`), {
          replace: true,
        });
      } else {
        navigate(companyRoute(company_id, PREPAIDS_PATH), { replace: true });
      }
    } catch (e: any) {
      const failureClass = classifyPrepaidCreateFailure(e, isClosedPeriodError);

      if (failureClass === "closed-period") {
        prepaidCreateAttemptRef.current = resolvePrepaidCreateDefiniteRejection();
        setError({ key: "sales.errors.closedSubmit", date: closedThroughDate });
        return;
      }

      if (failureClass === "idempotency-conflict") {
        prepaidCreateAttemptRef.current = resolvePrepaidCreateDefiniteRejection();
        setError({ key: "newPrepaid.conflict" });
        return;
      }

      if (failureClass === "definite-rejection") {
        prepaidCreateAttemptRef.current = resolvePrepaidCreateDefiniteRejection();
        setError(apiDisplayError(e));
        return;
      }

      // Ambiguous (network failure, timeout, unreadable response, HTTP
      // 500, or anything else unrecognized): retain the key, fingerprint,
      // and submitted payload so an unchanged retry reuses the same key
      // and safely replays instead of risking a duplicate, then restore
      // the form to exactly what was submitted.
      prepaidCreateAttemptRef.current = resolvePrepaidCreateAmbiguousFailure(
        prepaidCreateAttemptRef.current,
      );
      restoreSubmittedPayload(prepaidCreateAttemptRef.current);
      setError(apiDisplayError(e));
    } finally {
      prepaidCreateInFlightRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center justify-start px-2 py-8`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
        </button>

        <div className="flex-1 text-center">
          <h2 className={A.title}>{t("spendingPrepaids.new")}</h2>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate(companyRoute(company_id || "", PREPAIDS_PATH))
          }
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Card */}
      <div className={`${A.card} max-w-3xl w-full p-5 sm:p-6`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vendor (optional) with inlay create */}
          <div className="md:col-span-2">
            <label className={labelCls}>
              {t("directory.vendors.singular")} <span className="text-gray-400">{t("purchases.edit.optional")}</span>
            </label>

            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <AppleComboBox<Vendor>
                  options={vendors}
                  value={vendor}
                  onChange={setVendor}
                  getOptionLabel={vendorDisplayName}
                  getOptionSearchText={(o) => o.name}
                  preserveQueryOnDisplayChange
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  placeholder={
                    vendors.length ? t("newPrepaid.selectVendor") : t("expenses.entry.noVendors")
                  }
                />
              </div>

              <button
                type="button"
                title={t("directory.vendors.add")}
                className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition shrink-0`}
                onClick={() => setShowNewVendor(true)}
                aria-label={t("directory.vendors.addAria")}
              >
                <PlusIcon className="w-5 h-5 text-appleblue" />
              </button>
            </div>
          </div>

          {/* Payment Source */}
          <div>
            <label className={labelCls}>{t("expenses.entry.paymentSource")}</label>
            <AppleComboBox<Account>
              options={paymentAccounts}
              value={paymentAccount}
              onChange={setPaymentAccount}
              getOptionLabel={paymentDisplayName}
              getOptionSearchText={accountDisplayName}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              placeholder={paymentPlaceholder}
            />
          </div>

          {/* Amount */}
          <div>
            <label className={labelCls}>{t("sales.common.amount")}</label>
            <AppleNumberInput
              className="text-right tabular-nums"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={onAmountBlur}
              placeholder="0.00"
            />
          </div>

          {/* Date */}
          <div>
            <label className={labelCls}>{t("sales.common.date")}</label>

            <AppleDateInput
              valueYmd={date}
              onChangeYmd={setDate}
              ariaLabel={t("newPrepaid.dateAria")}
              openCalendarLabel={t("sales.shared.openCalendar")}
              inputClassName={`${A.inputBase} tabular-nums appearance-none`}
              placeholder="MM/DD/YYYY"
            />
            {isClosedDate && (
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t("sales.errors.closedWarning")}
              </div>
            )}
          </div>

          {/* Memo */}
          <div className="md:col-span-2">
            <MemoSection
              label={t("sales.common.memo")}
              placeholder={t("sales.common.memoPlaceholder")}
              mode="edit"
              value={memo}
              onChange={setMemo}
              className="mt-4 mb-8"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            {t("directory.common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={
              saving ||
              !company_id ||
              !paymentAccount ||
              !amount ||
              isNaN(parseFloat(amount)) ||
              parseFloat(amount) <= 0
            }
          >
            {saving ? t("directory.common.savingEllipsis") : t("sales.common.enter")}
          </Button>
        </div>

        {/* Error (reserve space for layout stability) */}
        <div style={{ minHeight: 24 }} className="mt-2">
          {error ? (
            <div className="text-red-600 text-[13px] font-medium">{displayError}</div>
          ) : null}
        </div>
      </div>

      {/* Inlay via shared VendorEntry */}
      {showNewVendor && company_id ? (
        <VendorEntry
          mode="inlay"
          variant="full"
          companyId={company_id}
          title={t("directory.vendors.new")}
          onCreated={(v: Vendor) => {
            setVendors((prev) => {
              const exists = prev.find((x) => String(x.id) === String(v.id));
              const list = exists ? prev : [v, ...prev];
              return list.sort((a, b) => a.name.localeCompare(b.name));
            });
            setVendor(v);
          }}
          onClose={() => setShowNewVendor(false)}
        />
      ) : null}
    </div>
  );
}
